import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import PageHeader from '@/components/feature/PageHeader';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import FileDrop from '@/components/base/FileDrop';
import { useAppData } from '@/store/AppDataContext';

interface DriverFormData {
  name: string;
  phone: string;
  username: string;
  password: string;
  photo: string;
  idDoc: string;
  licenseDoc: string;
  vehiclePhoto: string;
}

function blank(): DriverFormData {
  return { name: '', phone: '', username: '', password: '', photo: '', idDoc: '', licenseDoc: '', vehiclePhoto: '' };
}

export default function DriverFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { drivers, addDriver, updateDriver } = useAppData();

  const existing = id ? drivers.find((d) => d.id === id) : undefined;
  const isEdit = Boolean(existing);

  const [form, setForm] = useState<DriverFormData>(() =>
    existing ? { name: existing.name, phone: existing.phone, username: '', password: '', photo: '', idDoc: '', licenseDoc: '', vehiclePhoto: '' }
             : blank()
  );
  const [errors, setErrors] = useState<Partial<DriverFormData>>({});

  const set = (field: keyof DriverFormData) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    const errs: Partial<DriverFormData> = {};
    if (!form.name.trim()) errs.name = 'الاسم الكامل مطلوب';
    if (!form.phone.trim()) errs.phone = 'رقم الهاتف مطلوب';
    if (!isEdit && !form.password.trim()) errs.password = 'كلمة المرور مطلوبة';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    if (isEdit && existing) {
      updateDriver(existing.id, form);
      navigate('/drivers', { state: { toast: { type: 'success', message: 'تم تحديث بيانات المندوب' } } });
    } else {
      addDriver(form);
      navigate('/drivers', { state: { toast: { type: 'success', message: 'تمت إضافة المندوب بنجاح' } } });
    }
  };

  return (
    <DashboardLayout title={isEdit ? `تعديل: ${existing?.name}` : 'إضافة مندوب جديد'}>
      <div className="mx-auto w-full max-w-[640px]">
        <PageHeader
          title={isEdit ? `تعديل: ${existing?.name}` : 'إضافة مندوب جديد'}
          subtitle="أدخل بيانات المندوب والوثائق المطلوبة."
          backTo="/drivers"
        />

        <div className="rounded-card border border-background-200 bg-white p-5 shadow-card">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="الاسم الكامل"
                name="name"
                value={form.name}
                onChange={(event) => set('name')(event.target.value)}
                error={errors.name}
                required
                placeholder="أحمد الزهراني"
              />
              <Input
                label="رقم الهاتف"
                name="phone"
                value={form.phone}
                onChange={(event) => set('phone')(event.target.value)}
                error={errors.phone}
                required
                placeholder="05xxxxxxxx"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="اسم المستخدم"
                name="username"
                value={form.username}
                onChange={(event) => set('username')(event.target.value)}
                placeholder="تلقائي عند الترك"
              />
              <Input
                label={isEdit ? 'كلمة مرور جديدة (اختياري)' : 'كلمة المرور'}
                name="password"
                type="password"
                value={form.password}
                onChange={(event) => set('password')(event.target.value)}
                error={errors.password}
                required={!isEdit}
                placeholder="••••••••"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FileDrop label="صورة شخصية" fileName={form.photo} onSelect={set('photo')} hint="JPG أو PNG — بحد أقصى 5 ميجابايت" />
              <FileDrop label="صورة الهوية" fileName={form.idDoc} onSelect={set('idDoc')} hint="JPG أو PDF" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FileDrop label="رخصة القيادة" fileName={form.licenseDoc} onSelect={set('licenseDoc')} hint="JPG أو PDF" />
              <FileDrop label="صورة المركبة" fileName={form.vehiclePhoto} onSelect={set('vehiclePhoto')} hint="JPG أو PNG" />
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 border-t border-background-100 pt-5 sm:flex-row">
            <Button variant="outline" size="lg" className="flex-1" onClick={() => navigate('/drivers')}>
              إلغاء
            </Button>
            <Button variant="cta" size="lg" className="flex-1" icon="ri-save-line" onClick={submit}>
              حفظ
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}